import React, { useState, useEffect } from 'react';
import { Box, CircularProgress } from '@mui/material';
import examApis from '../../apis/exams';
import DropdownSelect from '../../components/DropdownSelect';
import ToastMessage from '../../components/ToastMessage';
import {
  academicYears,
  defaultAcademicYear
} from '../../constants/academicYears';
import { SnackbarSeverityType } from '../../enums';
import { SchoolClassesConfig } from '../../types/schoolConfigType';
import { ExamResponse } from '../Exams/types';
import ExamTableAndActions from './components/ExamTableAndActions';
import { ButtonContainer, StyledButton } from './styled';
import { PaperWrapper, Title } from '../common/styled';
import Icons from '../../components/icons';

interface ExamsCrudPageProps {
  allClasses?: SchoolClassesConfig;
  school: string;
  etherId: string;
  isClassesLoading?: boolean;
}

const ExamsCrudPage: React.FC<ExamsCrudPageProps> = ({
  allClasses,
  school,
  etherId,
  isClassesLoading
}) => {
  const [academicYear, setAcademicYear] = useState(defaultAcademicYear);
  const [grade, setGrade] = useState('');
  const [division, setDivision] = useState('');
  const [term, setTerm] = useState<{ name: string; id: string } | null>(null);
  const [termOptions, setTermOptions] = useState<
    { name: string; id: string }[]
  >([]);
  const [gradeOptions, setGradeOptions] = useState<string[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<string[]>([]);
  const [exams, setExams] = useState<ExamResponse[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTermsLoading, setIsTermsLoading] = useState(false);
  const [deletingExamId, setDeletingExamId] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = (message: string, severity: SnackbarSeverityType) => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  useEffect(() => {
    if (grade && division && academicYear && school) {
      setIsTermsLoading(true);
      examApis
        .getTermsFor(grade, division, academicYear, school)
        .then((response: any) => {
          const apiTerms = Array.isArray(response) ? response : [];
          setTermOptions(
            apiTerms.map((term: any) => ({ name: term.name, id: term.id }))
          );
        })
        .catch((error) => {
          console.error(error);
          showToast('Failed to load terms.', SnackbarSeverityType.error);
        })
        .finally(() => setIsTermsLoading(false));
    } else {
      setTermOptions([]);
      setTerm(null);
    }
  }, [grade, division, academicYear, allClasses, school]);

  useEffect(() => {
    if (allClasses) {
      setGradeOptions(allClasses.grades || []);
    }
  }, [allClasses]);

  useEffect(() => {
    if (allClasses && grade) {
      const divisions = allClasses.classes
        .filter(
          (schoolClass: { grade: string; division: string }) =>
            schoolClass.grade === grade
        )
        .map(
          (schoolClass: { grade: string; division: string }) =>
            schoolClass.division
        );
      setDivisionOptions(divisions);
    } else if (allClasses) {
      setDivisionOptions(allClasses.divisions || []);
    }
  }, [allClasses, grade]);

  const handleSearch = () => {
    if (grade && division && academicYear && school && term?.id) {
      setHasSearched(true);
      setIsLoading(true);
      examApis
        .getExamsFor(grade, division, academicYear, school, term.id)
        .then((response: any) => {
          const apiExams = Array.isArray(response) ? response : [];
          setExams(apiExams);
        })
        .catch((error) => {
          console.error(error);
          setExams([]);
          showToast('Failed to load exams.', SnackbarSeverityType.error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      setExams([]);
      setHasSearched(false);
    }
  };

  const handleDeleteExam = async (examIdToDelete: string) => {
    if (!school) {
      console.error('School information is missing for deleting exam.');
      showToast(
        'School information is missing for deleting exam.',
        SnackbarSeverityType.error
      );
      return;
    }
    setDeletingExamId(examIdToDelete);
    try {
      await examApis.deleteExam(school, examIdToDelete, etherId);
      handleSearch();
      showToast('Exam deleted successfully!', SnackbarSeverityType.success);
    } catch (error) {
      console.error('Error deleting exam:', error);
      showToast('Failed to delete exam.', SnackbarSeverityType.error);
    } finally {
      setDeletingExamId(null);
    }
  };

  return (
    <>
      <Title>Exams Management</Title>
      <PaperWrapper>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <DropdownSelect
            label='Academic Year'
            placeholder='Select Academic Year'
            name='academicYear'
            value={academicYear}
            options={academicYears}
            onChange={(e) => {
              setAcademicYear(e.target.value as string);
              setGrade('');
              setDivision('');
              setTerm(null);
              setDivisionOptions([]);
              setTermOptions([]);
              setHasSearched(false);
              setExams([]);
            }}
            loading={!!isClassesLoading && academicYears.length === 0}
          />
          <DropdownSelect
            label='Grade'
            name='grade'
            value={grade}
            options={gradeOptions.map((g) => ({ value: g, label: g }))}
            onChange={(e) => {
              setGrade(e.target.value as string);
              setDivision('');
              setTerm(null);
              setDivisionOptions([]);
              setTermOptions([]);
              setHasSearched(false);
              setExams([]);
            }}
            placeholder='Select Grade'
            loading={!!isClassesLoading && gradeOptions.length === 0}
          />
          <DropdownSelect
            label='Division'
            name='division'
            value={division}
            options={divisionOptions.map((d) => ({ value: d, label: d }))}
            onChange={(e) => {
              setDivision(e.target.value as string);
              setTerm(null);
              setTermOptions([]);
              setHasSearched(false);
              setExams([]);
            }}
            placeholder='Select Division'
            loading={!!isClassesLoading && divisionOptions.length === 0}
          />
          <DropdownSelect
            label='Term'
            name='term'
            value={term?.id || ''}
            options={termOptions.map((t) => ({ value: t.id, label: t.name }))}
            onChange={(e) => {
              setTerm(termOptions.find((t) => t.id === e.target.value) || null);
              setHasSearched(false);
              setExams([]);
            }}
            placeholder='Select Term'
            loading={isTermsLoading}
          />
        </Box>
        <ButtonContainer>
          <StyledButton
            onClick={handleSearch}
            disabled={
              isLoading || !grade || !division || !term?.id || isTermsLoading
            }
          >
            <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
            {isLoading ? (
              <CircularProgress size={24} color='inherit' />
            ) : (
              'Search'
            )}
          </StyledButton>
        </ButtonContainer>
      </PaperWrapper>
      {hasSearched && academicYear && grade && division && term?.id && (
        <>
          <ExamTableAndActions
            exams={exams}
            academicYear={academicYear}
            grade={grade}
            division={division}
            term={term}
            allClasses={allClasses}
            school={school}
            etherId={etherId}
            termOptions={termOptions}
            academicYearOptions={academicYears}
            onDeleteExam={handleDeleteExam}
            deletingExamId={deletingExamId}
            onExamAdded={handleSearch}
            onExamsCloned={handleSearch}
            onExamUpdated={handleSearch}
            showToast={showToast}
          />
        </>
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </>
  );
};

export default ExamsCrudPage;
